/**
 * Banner Component - Component Definition
 * =========================================
 *
 * Definición del componente Angular y registro del módulo.
 */

import angular, { IModule, IComponentOptions } from 'angular';
import 'angular-ui-bootstrap';

import { BannerController, BannerTitleController, BannerTextController } from './controller';
import {
  BANNER_TEMPLATE,
  BANNER_VERTICAL_TEMPLATE,
  BANNER_COMPACT_TEMPLATE,
  BANNER_TITLE_TEMPLATE,
  BANNER_TEXT_TEMPLATE,
  BANNER_ACTIONS_TEMPLATE
} from './template';
import {
  VARIANTS,
  SIZES,
  ICONS,
  DEFAULTS,
  BannerVariant,
  BannerSize,
  BannerIconType,
  IconPosition
} from './constants';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Interfaz para los bindings del componente Banner
 */
export interface IBannerComponentBindings {
  // Contenido
  title: string;
  description?: string;
  buttonText?: string;

  // Icono
  iconType?: BannerIconType;
  customIcon?: string;
  iconPosition?: IconPosition;
  showIcon?: boolean;

  // Apariencia
  variant?: BannerVariant;
  size?: BannerSize;

  // Comportamiento
  dismissible?: boolean;
  showButton?: boolean;

  // Acciones
  onButtonClick?: () => void;
  onDismiss?: () => void;
  onClick?: () => void;
}

// ============================================================================
// DEFINICIÓN DEL COMPONENTE
// ============================================================================

/**
 * Definición del componente ecBanner
 */
export const bannerComponentDefinition: IComponentOptions = {
  template: BANNER_TEMPLATE,
  controller: BannerController,
  transclude: true,
  bindings: {
    // === Contenido ===
    title: '@',
    description: '@',
    buttonText: '@',

    // === Icono ===
    iconType: '@',
    customIcon: '@',
    iconPosition: '@',
    showIcon: '<',

    // === Apariencia ===
    variant: '@',
    size: '@',

    // === Comportamiento ===
    dismissible: '<',
    showButton: '<',

    // === Acciones ===
    onButtonClick: '&?',
    onDismiss: '&?',
    onClick: '&?'
  }
};

/**
 * Definición del componente ecBannerVertical
 */
export const bannerVerticalComponentDefinition: IComponentOptions = {
  template: BANNER_VERTICAL_TEMPLATE,
  controller: BannerController,
  transclude: true,
  bindings: { ...bannerComponentDefinition.bindings }
};

/**
 * Definición del componente ecBannerCompact
 */
export const bannerCompactComponentDefinition: IComponentOptions = {
  template: BANNER_COMPACT_TEMPLATE,
  controller: BannerController,
  bindings: {
    title: '@',
    iconType: '@',
    customIcon: '@',
    showIcon: '<',
    variant: '@',
    size: '@',
    dismissible: '<',
    onDismiss: '&?',
    onClick: '&?'
  }
};

// ============================================================================
// DEFINICIONES DE HIJOS (Compound Components)
// ============================================================================

/**
 * Definición del componente ecBannerTitle
 * Variant/size-aware: require dual opcional al padre real (ecBanner o
 * ecBannerVertical, ambos comparten BannerController). Sin padre (uso
 * standalone) cae a un fallback estático — ver BannerTitleController.
 */
export const bannerTitleComponentDefinition: IComponentOptions = {
  template: BANNER_TITLE_TEMPLATE,
  controller: BannerTitleController,
  require: { banner: '^?ecBanner', bannerVertical: '^?ecBannerVertical' },
  transclude: true
};

/**
 * Definición del componente ecBannerText
 * Mismo require dual opcional que ecBannerTitle.
 */
export const bannerTextComponentDefinition: IComponentOptions = {
  template: BANNER_TEXT_TEMPLATE,
  controller: BannerTextController,
  require: { banner: '^?ecBanner', bannerVertical: '^?ecBannerVertical' },
  transclude: true
};

/**
 * Definición del componente ecBannerActions
 */
export const bannerActionsComponentDefinition: IComponentOptions = {
  template: BANNER_ACTIONS_TEMPLATE,
  transclude: true
};

// ============================================================================
// REGISTRO DEL MÓDULO ANGULAR
// ============================================================================

/**
 * Nombre del módulo Angular
 */
export const MODULE_NAME = 'ec.banner';

/**
 * Crear y registrar el módulo
 */
const bannerModule: IModule = angular
  .module(MODULE_NAME, ['ui.bootstrap', 'ngSanitize'])
  .component('ecBanner', bannerComponentDefinition)
  .component('ecBannerVertical', bannerVerticalComponentDefinition)
  .component('ecBannerCompact', bannerCompactComponentDefinition)
  .component('ecBannerTitle', bannerTitleComponentDefinition)
  .component('ecBannerText', bannerTextComponentDefinition)
  .component('ecBannerActions', bannerActionsComponentDefinition);

// ============================================================================
// EXPORTS
// ============================================================================

export {
  BannerController,
  BannerTitleController,
  BannerTextController,
  BANNER_TEMPLATE,
  BANNER_VERTICAL_TEMPLATE,
  BANNER_COMPACT_TEMPLATE,
  BANNER_TITLE_TEMPLATE,
  BANNER_TEXT_TEMPLATE,
  BANNER_ACTIONS_TEMPLATE,
  VARIANTS,
  SIZES,
  ICONS,
  DEFAULTS
};

// Re-export types
export type {
  BannerVariant,
  BannerSize,
  BannerIconType,
  IconPosition
} from './constants';

export default bannerModule;
